import * as THREE from 'three';
import Application from '../Application';
import { AmbienceAudio, ComputerAudio } from './AudioSources';
import SafeAudioListener from './SafeAudioListener';
import UIEventBus from '../UI/EventBus';
const POS_DEBUG = false;
const DEFAULT_REF_DISTANCE = 10000;
export default class Audio {
    constructor() {
        this.application = new Application();
        this.listener = new SafeAudioListener();
        this.application.camera.instance.add(this.listener);
        this.context = this.listener.context; // Use same context Three.js uses for playback
        this.loadedAudio = this.application.resources.items.audio;
        this.scene = this.application.scene;
        this.audioPool = {};
        this.audioSources = {
            computer: new ComputerAudio(this),
            ambience: new AmbienceAudio(this),
        };
        UIEventBus.on('loadingScreenDone', () => {
            // Unlock audio on first user interaction (browser autoplay policy)
            const unlock = () => {
                const ctx = this.listener.context;
                if (ctx && ctx.state === 'suspended')
                    ctx.resume();
            };
            document.addEventListener('click', unlock, { once: true });
            document.addEventListener('touchstart', unlock, { once: true });
            document.addEventListener('keydown', unlock, { once: true });
        });
        UIEventBus.on('muteToggle', (mute) => {
            const v = mute ? 0 : 1;
            if (Number.isFinite(v))
                this.listener.setMasterVolume(Math.max(0, Math.min(1, v)));
        });
    }
    playAudio(sourceName, options = {}) {
        var _a;
        // Resume context if it's suspended
        if (this.context)
            this.context.resume();
        // Get the audio source
        sourceName = this.getRandomVariant(sourceName);
        if (!sourceName)
            return '';
        // Setup
        const buffer = this.loadedAudio[sourceName];
        if (!buffer)
            return '';
        const poolKey = sourceName + '_' + Object.keys(this.audioPool).length;
        let audio = new THREE.Audio(this.listener);
        if (options.position) {
            audio = new THREE.PositionalAudio(this.listener);
            // @ts-ignore
            audio.setRefDistance(options.refDistance || DEFAULT_REF_DISTANCE);
            // @ts-ignore
            // audio.setDistanceModel('linear');
            const extraMaterialOptions = !POS_DEBUG
                ? {
                    transparent: true,
                    opacity: 0,
                }
                : {};
            const sphere = new THREE.SphereGeometry(100, 8, 8);
            const material = new THREE.MeshBasicMaterial(Object.assign({ color: 0xff0000 }, extraMaterialOptions));
            const mesh = new THREE.Mesh(sphere, material);
            mesh.position.copy(options.position);
            mesh.name = poolKey;
            this.scene.add(mesh);
        }
        audio.setBuffer(buffer);
        if (options.filter) {
            const ac = audio.context;
            const filter = ac.createBiquadFilter();
            filter.type = options.filter.type; // Low pass filter
            const freq = Number.isFinite(options.filter.frequency) ? Math.max(0, Math.min(22050, options.filter.frequency)) : 1000;
            filter.frequency.setValueAtTime(freq, ac.currentTime);
            // filter.frequency.linearRampToValueAtTime(2400, ac.currentTime + 2);
            audio.setFilter(filter);
        }
        // Set options
        audio.setLoop(options.loop ? true : false);
        const vol = (_a = options.volume) !== null && _a !== void 0 ? _a : 1;
        if (Number.isFinite(vol))
            audio.setVolume(Math.max(0, Math.min(2, vol)));
        audio.play();
        // add a filter to the audio
        // Calculate detune
        const detuneAmount = (Math.random() * 200 - 100) *
            (options.randDetuneScale ? options.randDetuneScale : 0);
        // Set detune after .play is called (guard against non-finite for AudioParam)
        if (Number.isFinite(detuneAmount))
            audio.setDetune(detuneAmount);
        if (options.pitch != null && Number.isFinite(options.pitch)) {
            audio.setDetune(options.pitch * 100);
        }
        // Add to pool
        if (audio.source) {
            audio.source.onended = () => {
                delete this.audioPool[poolKey];
                if (options.position) {
                    const positionalObject = this.scene.getObjectByName(poolKey);
                    if (positionalObject) {
                        this.scene.remove(positionalObject);
                    }
                }
            };
            this.audioPool[poolKey] = audio;
        }
        return poolKey;
    }
    setAudioFilterFrequency(audio, frequency) {
        const a = this.audioPool[audio];
        if (a) {
            const ac = a.context;
            const filter = a.getFilter();
            const f = Number.isFinite(frequency) ? Math.max(0, Math.min(22050, frequency)) : 1000;
            filter.frequency.setValueAtTime(f, ac.currentTime);
        }
    }
    setAudioVolume(audio, volume) {
        const a = this.audioPool[audio];
        if (a && Number.isFinite(volume)) {
            a.setVolume(Math.max(0, Math.min(2, volume)));
        }
    }
    /** Position inside the loop for the desktop background-music track (for iframe Now Playing UI). */
    getBackgroundMusicPlayback() {
        const key = this.audioSources.ambience.backgroundMusicPoolKey;
        if (!key)
            return null;
        const raw = this.audioPool[key];
        if (!raw || !raw.buffer)
            return null;
        const duration = raw.buffer.duration;
        if (!Number.isFinite(duration) || duration <= 0)
            return null;
        const a = raw;
        let t;
        if (raw.isPlaying) {
            t =
                a._progress +
                    Math.max(a.context.currentTime - a._startedAt, 0) *
                        raw.getPlaybackRate();
        }
        else {
            t = a._progress;
        }
        if (raw.getLoop()) {
            t = ((t % duration) + duration) % duration;
        }
        else {
            t = Math.min(Math.max(0, t), duration);
        }
        return { current: t, duration };
    }
    getRandomVariant(sourceName) {
        const variants = [];
        for (const key in this.loadedAudio) {
            if (key.includes(sourceName)) {
                variants.push(key);
            }
        }
        return variants[Math.floor(Math.random() * variants.length)];
    }
    update() {
        for (const key in this.audioSources) {
            const _key = key;
            this.audioSources[_key].update();
        }
    }
}
